import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Projects.css';
import HighlightedTitle from '../components/HighlightedTitle';
import ProjectPage from '../components/ProjectPage';

function ArtProjects() {
  const projects = React.useMemo(() => [
    {
      id: 'city-unwrapped',
      title: 'City Unwrapped',
      date: '2026',
      summary: 'Scanning myself and my environment and passing control',
      writing: `Part of this process is taking this surface and injecting a skeleton. A control module to puppeteer my body. But once that skeleton is inside, the body must follow. And thats where it gets weird. You can move the body without moving the skeleton, and the body contorts to fit. The pieces that need to be connected remain, and the rest is free to move. Theres a natural movement to it, as the limbs transition smoothly from node to node.

Maybe its the graphic appeal, the same reason why you can’t look away from a car crash.

I think we look to understand. No one wants to watch a car crash, but maybe we do anyways, because we want to understand. I think thats the same reason why the uncanny valley is so scary. We can recognize but we can’t understand. In many ways these are self portraits, but as recognizable as they are, the bodies remain confusing.

Mapping the surfaces of each image onto a digital plane is an attempt to make the body more understandable. We do not understand the depths and nuances that our body contains. We can abstract the body into a series of planes, into math. And suddenly, the distinct pieces of body and environment spill into each other. The math has imperfect information. It confuses which planes belong to which pixels.

Tools like Blender and Mixama allow me to carve my body out from the planes. I can now put myself back into the abstraction. Now we are left with something in between a photo and a collage. Although the images are recognizable, there is distance between my life and what is represented. That distance is confusing, just as my life is confusing to the math.`,
      images: [
        { type: 'image', url: '/project_images_compressed/art/city-unwrapped/scan1.jpg' },
        { type: 'image', url: '/project_images_compressed/art/city-unwrapped/scan2.jpg' },
        { type: 'image', url: '/project_images_compressed/art/city-unwrapped/scan3.jpg' },
        { type: 'image', url: '/project_images_compressed/art/city-unwrapped/scan4.jpg' },
        { type: 'image', url: '/project_images_compressed/art/city-unwrapped/scan5.jpg' },
        { type: 'image', url: '/project_images_compressed/art/city-unwrapped/scan6.jpg' },
        { type: 'image', url: '/project_images_compressed/art/city-unwrapped/scan7.jpg' },
      ],
    },
    {
      id: 'sand-remembers',
      title: 'The Sand Remembers the Waves',
      date: '2025',
      summary: 'Oil-based monotype relief prints on newsprint.',
      images: [
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis2.jpg' },
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis5.jpg' },
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis9.jpg' },
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis6.jpg' },
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis10.jpg' },
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis4.jpg' },
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis1.jpg' },
        { type: 'image', url: '/project_images_compressed/art/sand-remembers/thesis8.jpg' },
        { type: 'video', url: "https://www.youtube.com/watch?v=WSP4N5vNup0" },
      ],
    },
    {
      id: 'after-a-long-day',
      title: 'After A Long Day',
      date: '2024',
      summary: 'A short video about a relationship.',
      images: [
        { type: 'image', url: '/project_images_compressed/art/after-a-long-day/stormking.jpg' },
        { type: 'video', url: "https://www.youtube.com/watch?v=nKCuCGDjblM" },
        { type: 'image', url: '/project_images_compressed/art/after-a-long-day/stormking2.jpg' },
        { type: 'image', url: '/project_images_compressed/art/after-a-long-day/stormking3.jpg' },
        { type: 'image', url: '/project_images_compressed/art/after-a-long-day/stormking4.jpg' },
        { type: 'image', url: '/project_images_compressed/art/after-a-long-day/stormking5.jpg' },
      ],
    },
    {
      id: 'winter-24',
      title: "Winter '24",
      date: '2024',
      summary: 'First snow.',
      images: [
        { type: 'image', url: '/project_images_compressed/art/winter-24/winter.jpg' },
        { type: 'video', url: "https://www.youtube.com/watch?v=q6frBblMUhk" },
      ],
    },
    {
      id: 'watercolor-ritual',
      title: 'Watercolor Ritual',
      date: '2023',
      summary: 'Three handmade objects exploring watercolor as a ritual.',
      images: [
        { type: 'image', url: '/project_images_compressed/art/watercolor-ritual/watercolor.jpg' },
        { type: 'image', url: '/project_images_compressed/art/watercolor-ritual/watercolor2.jpg' },
        { type: 'image', url: '/project_images_compressed/art/watercolor-ritual/watercolor3.jpg' },
        { type: 'image', url: '/project_images_compressed/art/watercolor-ritual/watercolor4.jpg' },
      ],
    },
  ], []);

  const getSelectedProjectFromQuery = () => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    return projects.find((project) => project.id === projectId) || null;
  };

  const [selectedProject, setSelectedProject] = useState(getSelectedProjectFromQuery);
  const [imageLoaded, setImageLoaded] = useState(new Array(projects.length).fill(false));

  React.useEffect(() => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    setSelectedProject(projects.find((project) => project.id === projectId) || null);
  }, [projects]);

  const handleSelectProject = (project) => {
    const params = new URLSearchParams(window.location.search);
    params.set('project', project.id);
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(project);
  };

  const handleBack = () => {
    const nextUrl = window.location.pathname;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(null);
  };

  const handleImageLoad = (index) => {
    setImageLoaded((prev) => {
      const newLoadedState = [...prev];
      newLoadedState[index] = true;
      return newLoadedState;
    });
  };

  return (
    <AnimatePresence mode="wait">
      {selectedProject ? (
        <motion.div
          key="projectPage"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
        >
          <ProjectPage project={selectedProject} onBack={handleBack} />
        </motion.div>
      ) : (
        <motion.div
          key="projectList"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="projects-container">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`project-item ${index % 2 === 0 ? 'left-first' : 'right-first'}`}
                onClick={() => handleSelectProject(project)}
              >
                <div className={`project-image-shell ${imageLoaded[index] ? 'is-loaded' : 'is-loading'}`}>
                  <img
                    src={project.images[0].url}
                    alt={project.title}
                    className="project-image"
                    onLoad={() => handleImageLoad(index)}
                    loading="lazy"
                  />
                </div>
                <div className="project-text">
                  <div className="project-title">
                    <HighlightedTitle text={project.title} />
                    <p>{project.date}</p>
                  </div>
                  <p className='project-summary'>{project.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ArtProjects;